import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { apiUpdateEmployee } from "@/lib/api";
import { reportingCandidates } from "@/lib/reporting";
import type { EmployeeProfile } from "@/data/employees";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function ReportsToEditor({ employee, employees }: { employee: EmployeeProfile; employees: EmployeeProfile[] }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [managerId, setManagerId] = useState(employee.reportsTo?.id ?? "__none__");
  const [savedId, setSavedId] = useState(employee.reportsTo?.id ?? "__none__");
  useEffect(() => { const id = employee.reportsTo?.id ?? "__none__"; setManagerId(id); setSavedId(id); }, [employee.id, employee.reportsTo?.id]);
  const mutation = useMutation({
    mutationFn: () => apiUpdateEmployee(employee.id, { reportsToId: managerId === "__none__" ? null : managerId }),
    onSuccess: updated => {
      setSavedId(updated.reportsTo?.id ?? "__none__");
      // Existing desktop/mobile caches use different keys. Invalidate only
      // employee/team data so reporting and permission views refresh together.
      for (const key of ["employees", "employee", "employee-profile", "employee-by-email", "mobile-org-employees", "mobile-employees", "mobile-employee-profile", "team-members", "teams", "org-hierarchy"]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
  if (user?.role.mainRole !== "hr" && user?.role.mainRole !== "admin") return null;
  const current = employees.find(e => e.id === employee.id) ?? employee;
  const candidates = reportingCandidates(employees, current);
  const error = mutation.error as (Error & { code?: string }) | null;
  return <div className="space-y-2 rounded-lg border p-3">
    <Label htmlFor={`reports-to-${employee.id}`}>{t("reporting.label")}</Label>
    <Select value={managerId} onValueChange={setManagerId} disabled={mutation.isPending}>
      <SelectTrigger id={`reports-to-${employee.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">{t("reporting.noManager")}</SelectItem>
        {managerId !== "__none__" && !candidates.some(e => e.id === managerId) &&
          <SelectItem value={managerId} disabled>{employee.reportsTo?.name ?? t("reporting.invalidManager")}</SelectItem>}
        {candidates.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
      </SelectContent>
    </Select>
    {current.mainRole === "hr" && <p className="text-xs text-muted-foreground">{t("reporting.hrHint")}</p>}
    {error && <p role="alert" className="text-sm text-destructive">{t(error.code?.startsWith("reporting.") ? error.code : "reporting.saveError")}</p>}
    <Button type="button" size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending || managerId === savedId}>
      {t(mutation.isPending ? "common.saving" : "common.save")}
    </Button>
  </div>;
}
